import { getPrinter } from 'shared/printerManager';
import { useStoreGetTransaction } from './useStoreGetTransaction';

const getDigits = (barcode) => {
  const digits = barcode?.match(/\d+/g);
  return digits ? digits.join('') : '';
};

const getString = (barcode) => {
  const digits = barcode?.match(/[a-zA-Z]+/g);
  return digits ? digits.join('') : '';
};

const format_reserve_seat_info = ({ seat_id }) => {
  if (!seat_id) return null;
  const pieces = seat_id.split('-');
  return { section: pieces[0], row: pieces[1], seat: pieces[2] };
};

export const getTickets = (transaction) =>
  transaction.users
    ?.reduce((acc, user) => acc.concat(user.tickets), [])
    .filter((ticket) => ticket.type !== 'bundle' && !ticket.refunded);

export const sendPrintJob = async (prn) => {
  return new Promise((resolve, reject) => {
    prn.startMonitor();
    prn.send();
    prn.onreceive = (res) => {
      if (res.success) {
        resolve();
      } else {
        reject();
      }
    };
    prn.stopMonitor();
  });
};

export const addPrintLogo = (prn) => {
  const canvas = document.getElementById('canvas');
  const context = canvas.getContext('2d');
  context.drawImage(document.getElementById('logo'), 0, 0, 300, 150);
  prn.addImage(context, 0, 0, 300, 150, prn.COLOR_1);
};

export const usePrintTickets = () => {
  const { storeLastTransaction, getLastTransaction } = useStoreGetTransaction();

  const printTickets = async ({ transaction, setIsPrinting }) => {
    if (setIsPrinting) setIsPrinting(true);

    storeLastTransaction(transaction);

    const tickets = getTickets(transaction);
    const BATCH_SIZE = 5;

    // Split tickets into batches of 5
    const batches = [];
    for (let i = 0; i < tickets.length; i += BATCH_SIZE) {
      batches.push(tickets.slice(i, i + BATCH_SIZE));
    }

    for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
      const batch = batches[batchIndex];
      const prn = getPrinter();

      if (!prn) {
        alert('Not connected to printer');
        if (setIsPrinting) setIsPrinting(false);
        return;
      }

      // Process all tickets in this batch
      for (const ticket of batch) {
        const reserved_info = format_reserve_seat_info(ticket);
        try {
          prn.addTextAlign(prn.ALIGN_CENTER);
          prn.addTextSmooth(true);

          addPrintLogo(prn);
          prn.addFeedLine(2);
          prn.addTextSize(2, 2);
          prn.addTextStyle(false, false, true, prn.COLOR_1);
          prn.addTextFont(prn.FONT_C);
          prn.addText(ticket.name);

          prn.addTextFont(prn.FONT_A);
          prn.addTextStyle(false, false, false, prn.COLOR_1);

          if (reserved_info) {
            if (ticket.tier) {
              prn.addFeedLine(2);
              prn.addTextSize(2, 2);
              prn.addText(ticket.tier);
            }
            prn.addFeedLine(2);
            prn.addTextSize(2, 2);
            prn.addText(
              reserved_info.section?.includes('section')
                ? reserved_info.section
                : `Section ${reserved_info.section}`
            );
            prn.addFeedLine(2);
            prn.addText(
              `Row: ${reserved_info.row}   Seat: ${reserved_info.seat}`
            );
          }

          prn.addFeedLine(2);
          prn.addSymbol(
            ticket.barcode,
            prn.SYMBOL_QRCODE_MODEL_2,
            prn.LEVEL_Q,
            14,
            7,
            50
          );
          prn.addFeedLine(2);
          prn.addTextSize(3, 3);
          prn.addText(
            `${getDigits(ticket.barcode)} ${getString(ticket.barcode)}`
          );
          prn.addFeedLine(3);
          prn.addTextSize(2, 2);
          prn.addText(ticket.date);
          prn.addFeedLine(2);
          prn.addText(transaction.event.name);
          prn.addFeedLine(2);
          prn.addTextSize(1, 1);
          prn.addText(transaction.track.name);
          prn.addFeedLine(2);
          prn.addTextSize(2, 2);
          prn.addText(
            `Order # ${transaction.id} | ${transaction.card ? 'CARD' : 'CASH'}`
          );
          prn.addFeedLine(2);
          prn.addTextSize(1, 1);
          prn.addText('RAIN CHECK - KEEP THIS TICKET');
          prn.addFeedLine(2);
          prn.addText('Get the Ticket Hoss App and Skip the Line!');
          prn.addFeedLine(5);
          prn.addCut(prn.CUT_FEED);
        } catch (error) {
          alert(`Error formatting ticket for ${ticket.name}: ${error}`);
        }
      }

      // Send this batch to printer
      try {
        await sendPrintJob(prn);
        // Optional: Add a small delay between batches to prevent printer overload
        if (batchIndex < batches.length - 1) {
          await new Promise((resolve) => setTimeout(resolve, 1000)); // 1 second delay
        }
      } catch (error) {
        console.error(`Error printing batch ${batchIndex + 1}:`, error);
        alert(`Error printing batch ${batchIndex + 1}: ${error}`);
        // Continue with next batch instead of stopping completely
      }
    }

    if (setIsPrinting) setIsPrinting(false);
  };

  const reprintLastTransaction = async (setIsPrinting) => {
    const transaction = getLastTransaction();
    if (!transaction) {
      alert('No last ticket transaction found.');
      return;
    }

    await printTickets({ transaction, setIsPrinting });
  };

  return {
    printTickets,
    reprintLastTransaction,
  };
};
